import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useContext,
} from "react";
import { Box, Typography } from "@mui/material";
import {
  useTaskAgentState,
  processEventsWithAddedData,
  AddedTask,
  AddedDocument,
} from "../hooks/useTaskAgentState";
import { useGetAgentsThread } from "../../services/agents";
import { GetAgentsThreadRequest } from "protogen/agents_service_pb";
import { useWebSocketContext } from "../context/SocketConsumerProvider";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import TaskAgentDisplay from "components/tools/TaskAgentDisplay";
import AgentDebugToolButton from "components/tasks/blocks/AgentDebugToolButton";

interface EmailAgentDisplayProps {
  threadRef: string;
  familyRef?: string;
}

export default function EmailAgentDisplay({
  threadRef,
  familyRef,
}: EmailAgentDisplayProps) {
  const [hasLoadedThread, setHasLoadedThread] = useState(false);
  const processedEventCount = useRef(0);
  const stateManagerRef = useRef<any>(null);

  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { request: getAgentsThread } = useGetAgentsThread();

  const processEvents = useCallback(
    (
      events: string[],
      addedEventsMap: Map<
        number,
        {
          eventRef: string;
          eventStatus: string;
          eventModified: boolean;
          sourceEntityType: string;
          sourceEntityId: string;
          sourceEntityIndex: number;
        }
      >,
    ) => {
      const processedEvents = processEventsWithAddedData(
        events,
        addedEventsMap,
      );
      processedEvents.forEach((eventString: string) => {
        const event = JSON.parse(eventString);
        stateManagerRef.current?.actions.handleWebSocketMessage("", event);
      });
    },
    [],
  );

  const stateManager = useTaskAgentState({
    staticState: { isActive: true },
    onRefresh: async () => {
      if (!threadRef) return;

      const response = await getAgentsThread(
        new GetAgentsThreadRequest({ threadRef }),
      );

      if (response?.events) {
        const allEvents = response.events;

        if (allEvents.length > processedEventCount.current) {
          const newEvents = allEvents.slice(processedEventCount.current);

          const addedEventsMap = new Map<
            number,
            {
              eventRef: string;
              eventStatus: string;
              eventModified: boolean;
              sourceEntityType: string;
              sourceEntityId: string;
              sourceEntityIndex: number;
            }
          >();
          if (response.addedEvents && response.addedEvents.length > 0) {
            response.addedEvents.forEach((addedEvent) => {
              const relativeIndex =
                addedEvent.sourceEntityIndex - processedEventCount.current;
              if (relativeIndex >= 0) {
                addedEventsMap.set(relativeIndex, {
                  eventRef: addedEvent.eventRef,
                  eventStatus: addedEvent.eventStatus,
                  eventModified: addedEvent.eventModified,
                  sourceEntityType: addedEvent.sourceEntityType,
                  sourceEntityId: addedEvent.sourceEntityId,
                  sourceEntityIndex: addedEvent.sourceEntityIndex,
                });
              }
            });
          }

          processEvents(newEvents, addedEventsMap);
          processedEventCount.current = allEvents.length;
        }
      }

      // Update added tasks from server
      if (response?.addedTasks) {
        const tasks: AddedTask[] = response.addedTasks.map((task) => ({
          taskRef: task.taskRef,
          taskTitle: task.taskTitle,
          taskState: task.taskState,
          taskPriority: task.taskPriority,
          taskStage: task.taskStage,
          taskDueDate: task.taskDueDate,
          taskProject: task.taskProject,
          taskStageDate: task.taskStageDate,
        }));
        stateManagerRef.current?.actions.updateState({ addedTasks: tasks });
      }

      // Update added documents from server
      if (response?.addedDocuments) {
        const documents: AddedDocument[] = response.addedDocuments.map(
          (doc) => ({
            ref: doc.ref,
            uuid: doc.uuid,
            title: doc.title,
            subtitle: doc.subtitle,
            documentType: doc.documentType,
            scope: doc.scope,
            ownerRef: doc.ownerRef,
            ownerDisplayName: doc.ownerDisplayName,
            lastUpdatedSec: Number(doc.lastUpdatedSec),
            unreadCount: doc.unreadCount,
          }),
        );
        stateManagerRef.current?.actions.updateState({
          addedDocuments: documents,
        });
      }
    },
  });

  stateManagerRef.current = stateManager;

  useEffect(() => {
    const loadThreadState = async (threadRef: string) => {
      if (hasLoadedThread) return;
      setHasLoadedThread(true);

      const response = await getAgentsThread(
        new GetAgentsThreadRequest({ threadRef }),
      );

      const addedEventsMap = new Map<
        number,
        {
          eventRef: string;
          eventStatus: string;
          eventModified: boolean;
          sourceEntityType: string;
          sourceEntityId: string;
          sourceEntityIndex: number;
        }
      >();
      if (response?.addedEvents && response.addedEvents.length > 0) {
        response.addedEvents.forEach((addedEvent) => {
          addedEventsMap.set(addedEvent.sourceEntityIndex, {
            eventRef: addedEvent.eventRef,
            eventStatus: addedEvent.eventStatus,
            eventModified: addedEvent.eventModified,
            sourceEntityType: addedEvent.sourceEntityType,
            sourceEntityId: addedEvent.sourceEntityId,
            sourceEntityIndex: addedEvent.sourceEntityIndex,
          });
        });
      }

      const events = response?.events || [];
      processEvents(events, addedEventsMap);
      processedEventCount.current = events.length;

      // Update added tasks from server
      if (response?.addedTasks) {
        const tasks: AddedTask[] = response.addedTasks.map((task) => ({
          taskRef: task.taskRef,
          taskTitle: task.taskTitle,
          taskState: task.taskState,
          taskPriority: task.taskPriority,
          taskStage: task.taskStage,
          taskDueDate: task.taskDueDate,
          taskProject: task.taskProject,
          taskStageDate: task.taskStageDate,
        }));
        stateManagerRef.current?.actions.updateState({ addedTasks: tasks });
      }

      // Update added documents from server
      if (response?.addedDocuments) {
        const documents: AddedDocument[] = response.addedDocuments.map(
          (doc) => ({
            ref: doc.ref,
            uuid: doc.uuid,
            title: doc.title,
            subtitle: doc.subtitle,
            documentType: doc.documentType,
            scope: doc.scope,
            ownerRef: doc.ownerRef,
            ownerDisplayName: doc.ownerDisplayName,
            lastUpdatedSec: Number(doc.lastUpdatedSec),
            unreadCount: doc.unreadCount,
          }),
        );
        stateManagerRef.current?.actions.updateState({
          addedDocuments: documents,
        });
      }
    };

    if (threadRef) {
      loadThreadState(threadRef);
    }
  }, [threadRef, getAgentsThread, hasLoadedThread, processEvents]);

  useEffect(() => {
    const websocketHandler = (channel: string, data: any) => {
      stateManager.actions.handleWebSocketMessage(channel, data);
    };

    const channel = `task-agent/${encodeURIComponent(threadRef)}`;
    registerHandler(channel, websocketHandler);

    return () => {
      unregisterHandler(channel, websocketHandler);
    };
  }, [
    registerHandler,
    unregisterHandler,
    threadRef,
    stateManager.actions.handleWebSocketMessage,
  ]);

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid #D4D4D4",
        borderRadius: "8px",
        padding: "16px",
        marginTop: "12px",
        marginBottom: "12px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="bodyHeavy"
          sx={{ marginBottom: "12px", color: "#198282" }}
        >
          Email Agent
        </Typography>
        {isAdmin && <AgentDebugToolButton threadRef={threadRef} />}
      </Box>
      <TaskAgentDisplay
        familyRef={familyRef}
        wrapInContainer={false}
        agentThreadRef={threadRef}
        stateManager={stateManager}
      />
    </Box>
  );
}
